/**
 * UserDataFetcher.js
 *
 * This component is responsible for fetching user-specific data from Firestore based on the authenticated user's ID.
 * It utilizes the Firestore database to retrieve user data and provides this data throughout the application
 * using React Context. This allows child components to access user data seamlessly.
 *
 * Dependencies:
 * - React: For building the component and utilizing hooks for state and effect management.
 * - Firebase Firestore: For fetching user data from the database.
 * - UserManager: Custom hook `useAuthUser` to access the authenticated user's details.
 *
 * The component listens for changes in the authenticated user's state and fetches the corresponding user data
 * from Firestore. The fetched data is then made available to any child components through the `UserDataContext`.
 */

import React, { createContext, useContext, useState, useEffect } from "react";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { useAuthUser } from "./UserManager";
import { UserRole } from "@shared/types/DB2";
import { collection, query, where, getDocs } from "firebase/firestore";

// Defines the structure for the user data expected from Firestore.
interface UserData {
  dealerId?: string;
  userType?: string;
  manufacturerId?: string;
  manufacturerIds?: string[];
  manufacturerRepId?: string;
  email?: string;
  role: UserRole | "admin" | "developer";
  isAdmin?: boolean;
  isDev?: boolean;
  isManufacturerPrincipal?: boolean;
  principalManufacturerId?: string | null;
  // ... other fields if necessary
}

// Creates a React context for user data with an initial state of null.
type UserDataContextValue = {
  userData: UserData | null;
  userDataError: { code?: string; raw?: unknown } | null;
};

const UserDataContext = createContext<UserDataContextValue>({
  userData: null,
  userDataError: null,
});

interface UserDataFetcherProps {
  children: React.ReactNode;
}

// The UserDataFetcher component.
const UserDataFetcher: React.FC<UserDataFetcherProps> = ({ children }) => {
  const [userData, setUserData] = useState<UserData | null>(null);
  const [userDataError, setUserDataError] =
    useState<{ code?: string; raw?: unknown } | null>(null);
  const { authUser } = useAuthUser(); // Retrieves the current authenticated user.
  const db = getFirestore(); // Initializes Firestore.


  // Effect hook to fetch user data when the authenticated user changes.
  useEffect(() => {
    if (!authUser) {
      console.log("UserDataFetcher: authUser missing, clearing userData");
      setUserData(null);
      setUserDataError(null);
      return;
    }

    const fetchUserData = async () => {
      const uid = authUser?.uid;

      if (uid) {
        console.log("UserDataFetcher: Activated for UID:", uid); // Log when data fetching is activated
        const userDocRef = doc(db, "users", uid); // References the user's document in Firestore.

        try {
          const userDocSnap = await getDoc(userDocRef); // Attempts to fetch the user document.

          if (userDocSnap.exists()) {
            const data = userDocSnap.data() as UserData; // Casts the document data to UserData type.
            let isManufacturerPrincipal = false;
            let principalManufacturerId: string | null = null;
            const manufacturerCandidates: string[] = [];

            if (typeof data.manufacturerId === "string" && data.manufacturerId.trim().length) {
              manufacturerCandidates.push(data.manufacturerId.trim());
            }
            const extraManufacturers = (data as any)?.manufacturerIds;
            if (Array.isArray(extraManufacturers)) {
              extraManufacturers.forEach((value: unknown) => {
                if (typeof value === "string" && value.trim().length) {
                  manufacturerCandidates.push(value.trim());
                }
              });
            }

            for (const manufacturerId of manufacturerCandidates) {
              try {
                const manufacturerDocRef = doc(db, "manufacturers", manufacturerId);
                const manufacturerSnap = await getDoc(manufacturerDocRef);
                if (manufacturerSnap.exists()) {
                  const principalUid = (manufacturerSnap.data() as any)?.principalUserId;
                  if (principalUid === uid) {
                    isManufacturerPrincipal = true;
                    principalManufacturerId = manufacturerId;
                    break;
                  }
                }
              } catch (err) {
                console.warn("UserDataFetcher: failed to resolve manufacturer principal", {
                  manufacturerId,
                  error: err,
                });
              }
            }

            setUserData({
              ...data,
              isManufacturerPrincipal,
              principalManufacturerId,
            }); // Updates state with fetched user data.
            setUserDataError(null);
            console.log("UserDataFetcher: Data retrieved successfully", {
              ...data,
              isManufacturerPrincipal,
              principalManufacturerId,
            }); // Log the retrieved data
          } else {
            console.log(
              `UserDataFetcher: No user document found for UID: ${uid}`
            );
            setUserData(null);
            setUserDataError({
              code: "not-found",
              raw: null,
            });
          }
        } catch (error) {
          console.error("UserDataFetcher: Error during Firestore query", error);
          setUserData(null);
          const code =
            typeof (error as any)?.code === "string"
              ? ((error as any).code as string)
              : undefined;
          setUserDataError({
            code,
            raw: error,
          });
        }
      } else {
        console.log(
          "UserDataFetcher: No auth user ID available for data fetching"
        );
      }
    };

    fetchUserData();
  }, [authUser, db]);

  useEffect(() => {
    console.log("UserDataFetcher: userData state changed", {
      hasUserData: !!userData,
      userData,
    });
  }, [userData]);

  // Provides the fetched user data to child components.
  return (
    <UserDataContext.Provider value={{ userData, userDataError }}>
      {children}
    </UserDataContext.Provider>
  );
};

// Custom hook to enable easy access to user data from within child components.
export const useUserData = () => {
  const context = useContext(UserDataContext);
  if (!context) {
    throw new Error("useUserData must be used within a UserDataFetcher");
  }
  return context.userData;
};

export const useUserDataMeta = () => {
  const context = useContext(UserDataContext);
  if (!context) {
    throw new Error("useUserDataMeta must be used within a UserDataFetcher");
  }
  return context;
};

export default UserDataFetcher;
